import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { API, useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function Bundles() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [giftFor, setGiftFor] = useState(null); // { kind, city, label, amount }

  useEffect(() => {
    axios.get(`${API}/bundles`).then(r => setData(r.data));
  }, []);

  const buyBundle = async (kind, city) => {
    if (!user) {
      try {
        window.localStorage.setItem(
          "pending_purchase",
          JSON.stringify({ path: "/bundles", at: Date.now() })
        );
      } catch (_) {}
      toast.message(t("sign_in_subtitle"));
      nav("/login");
      return;
    }
    const marker = `${kind}:${city||""}`;
    try {
      setBusy(marker);
      const res = await axios.post(`${API}/checkout/bundle`, {
        kind, city: city || null, origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  const openGift = (kind, city, label, amount) => {
    if (!user) {
      try {
        window.localStorage.setItem(
          "pending_purchase",
          JSON.stringify({ path: "/bundles", at: Date.now() })
        );
      } catch (_) {}
      toast.message(t("sign_in_subtitle"));
      nav("/login");
      return;
    }
    setGiftFor({ kind, city, label, amount });
  };

  if (!data) return <div className="max-w-7xl mx-auto p-10 text-zinc-500" data-testid="bundles-loading">Loading…</div>;

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="bundles-page">
      <div className="overline">{t("bundle_overline")}</div>
      <h1 className="font-serif text-5xl lg:text-6xl tracking-tighter mt-3">{t("bundle_title")}</h1>
      <p className="text-zinc-400 mt-4 max-w-2xl text-lg font-light">{t("bundle_sub")}</p>
      <div className="divider-gold my-10" />

      {/* Grand Tour — hero card */}
      <section className="mb-20" data-testid="grand-tour-section">
        <div className="glass relative overflow-hidden grain p-8 lg:p-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="overline">{t("bundle_grand_overline")}</div>
            <h2 className="font-serif italic gold text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">{t("bundle_grand_title")}</h2>
            <p className="text-zinc-300 mt-6 font-light text-lg max-w-xl">{t("bundle_grand_sub")}</p>
            <ul className="text-zinc-400 mt-6 space-y-2 text-sm">
              <li>· {data.grand_tour.recipe_count} {t("bundle_recipes")}</li>
              <li>· {t("bundle_lifetime")}</li>
              <li>· {t("bundle_bilingual")}</li>
            </ul>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="flex items-baseline gap-4">
              <div className="font-serif text-7xl gold">£{data.grand_tour.bundle_price}</div>
              {data.grand_tour.savings > 0 && (
                <div className="text-zinc-500 line-through text-2xl font-serif" data-testid="grand-original-price">£{data.grand_tour.individual_total}</div>
              )}
            </div>
            {data.grand_tour.savings > 0 && (
              <div className="mt-2 tag tag-gold inline-block w-fit" data-testid="grand-savings">{t("bundle_save")} £{data.grand_tour.savings}</div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                onClick={() => buyBundle("grand_tour", null)}
                disabled={busy==="grand_tour:"}
                className="btn-gold flex-1 justify-center"
                data-testid="grand-tour-buy"
              >{busy==="grand_tour:" ? "…" : t("bundle_grand_cta")} →</button>
              <button
                onClick={() => openGift("grand_tour", null, t("bundle_grand_title_short") || "Grand Tour", data.grand_tour.bundle_price)}
                className="btn-ghost flex-1 justify-center"
                data-testid="grand-tour-gift"
              >🎁 {t("gift_this_pass")}</button>
            </div>
            <p className="text-xs text-zinc-500 mt-4">{t("bundle_one_time")}</p>
          </div>
        </div>
      </section>

      {/* Capital Passes */}
      <section data-testid="capital-passes-section">
        <div className="flex justify-between items-end mb-8">
          <div>
            <div className="overline">{t("bundle_city_overline")}</div>
            <h2 className="font-serif text-4xl tracking-tighter mt-2">{t("bundle_city_title")}</h2>
          </div>
          <div className="text-zinc-500 text-sm">£200 / city · {t("bundle_currency_note")}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {data.capitals.map(c => (
            <div key={c.city} className="card-recipe overflow-hidden" data-testid={`capital-pass-${c.city.toLowerCase().replace(/[^a-z]/g,'-')}`}>
              <div className="relative h-44 overflow-hidden">
                {c.preview_image && <img src={c.preview_image} alt={c.city} className="w-full h-full object-cover opacity-80" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="overline">{t("bundle_capital_label")}</div>
                  <div className="font-serif text-2xl mt-1">{c.city}</div>
                </div>
              </div>
              <div className="p-6">
                <div className="text-zinc-400 text-xs mb-4">
                  {c.recipe_count} {t("bundle_recipes_short")} · {c.meal_count} {t("filter_meals").split(" ")[0]}, {c.dessert_count} {t("filter_desserts").split(" ")[0]}
                </div>
                <div className="flex items-baseline gap-3">
                  <div className="font-serif text-4xl gold">£{c.bundle_price}</div>
                  {c.savings > 0 ? (
                    <div className="text-zinc-600 line-through text-sm">£{c.individual_total}</div>
                  ) : (
                    <div className="text-zinc-500 text-xs italic">{t("bundle_more_soon")}</div>
                  )}
                </div>
                {c.savings > 0 && <div className="tag tag-gold inline-block mt-2" data-testid={`capital-savings-${c.city.toLowerCase().replace(/[^a-z]/g,'-')}`}>{t("bundle_save")} £{c.savings}</div>}
                <div className="flex gap-2 mt-5">
                  <button
                    onClick={() => buyBundle("capital", c.city)}
                    disabled={busy===`capital:${c.city}`}
                    className="btn-ghost flex-1 justify-center text-[10px]"
                    data-testid={`capital-buy-${c.city.toLowerCase().replace(/[^a-z]/g,'-')}`}
                  >{busy===`capital:${c.city}` ? "…" : `${t("bundle_unlock_city")} ${c.city}`}</button>
                  <button
                    onClick={() => openGift("capital", c.city, `${t("bundle_capital_label")} ${c.city}`, c.bundle_price)}
                    className="btn-ghost !px-3 text-base"
                    data-testid={`capital-gift-${c.city.toLowerCase().replace(/[^a-z]/g,'-')}`}
                    title={t("gift_this_pass")}
                  >🎁</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <p className="text-zinc-600 text-xs mt-12 max-w-2xl">{t("bundle_legal")}</p>

      {giftFor && <GiftModal gift={giftFor} onClose={() => setGiftFor(null)} user={user} t={t} />}
    </main>
  );
}

function GiftModal({ gift, onClose, user, t }) {
  const [form, setForm] = useState({
    recipient_email: "",
    recipient_name: "",
    sender_name: user?.name || "",
    personal_message: "",
  });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.recipient_email || !form.recipient_email.includes("@")) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    try {
      setSending(true);
      const res = await axios.post(`${API}/checkout/gift`, {
        kind: gift.kind,
        city: gift.city || null,
        recipient_email: form.recipient_email.trim(),
        recipient_name: form.recipient_name.trim(),
        sender_name: form.sender_name.trim(),
        personal_message: form.personal_message.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setSending(false);
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 fade-in" onClick={onClose} data-testid="gift-modal">
      <div className="glass max-w-lg w-full p-8 lg:p-10 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="overline">🎁 {t("gift_modal_overline")}</div>
        <h2 className="font-serif text-3xl tracking-tighter mt-2">{gift.label}</h2>
        <p className="text-zinc-400 text-sm mt-2">£{gift.amount} · {t("bundle_one_time")}</p>
        <div className="divider-gold my-6" />

        <div className="space-y-4">
          <div>
            <label className="overline block mb-2">{t("gift_recipient_email")} *</label>
            <input
              className="fld"
              type="email"
              placeholder="friend@example.com"
              value={form.recipient_email}
              onChange={e => setForm({...form, recipient_email: e.target.value})}
              data-testid="gift-recipient-email"
            />
          </div>
          <div>
            <label className="overline block mb-2">{t("gift_recipient_name")}</label>
            <input
              className="fld"
              placeholder="Sarah Lee"
              value={form.recipient_name}
              onChange={e => setForm({...form, recipient_name: e.target.value})}
              data-testid="gift-recipient-name"
            />
          </div>
          <div>
            <label className="overline block mb-2">{t("gift_sender_name")}</label>
            <input
              className="fld"
              placeholder={user?.name || "Your name"}
              value={form.sender_name}
              onChange={e => setForm({...form, sender_name: e.target.value})}
              data-testid="gift-sender-name"
            />
          </div>
          <div>
            <label className="overline block mb-2">{t("gift_personal_message")}</label>
            <textarea
              className="fld h-28"
              placeholder={t("gift_message_placeholder")}
              maxLength={500}
              value={form.personal_message}
              onChange={e => setForm({...form, personal_message: e.target.value})}
              data-testid="gift-personal-message"
            />
            <div className="text-xs text-zinc-600 mt-1">{form.personal_message.length}/500</div>
          </div>
        </div>

        <p className="text-xs text-zinc-500 mt-6 leading-relaxed">{t("gift_modal_legal")}</p>

        <div className="flex gap-3 mt-6">
          <button onClick={submit} disabled={sending} className="btn-gold flex-1 justify-center" data-testid="gift-submit">
            {sending ? "…" : `${t("gift_pay")} £${gift.amount} →`}
          </button>
          <button onClick={onClose} className="btn-ghost" data-testid="gift-cancel">{t("admin_cancel") || "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}
